// ============================================================================
// BUDGET FOLLOW-UP PAGE — Seguimiento de presupuestos enviados v3
// ============================================================================
import { useState, useEffect, useCallback } from 'react';
import { Phone, PhoneOff, PhoneMissed, MessageSquare, Calendar, Clock, RefreshCw, AlertTriangle, CheckCircle, X, Save, Loader2, FileText, Download, History, Mail } from 'lucide-react';
import { budgetsApi } from '../../api/budgets';
import { budgetContactsApi, type FollowUpBudgetDto, type BudgetContactDto } from '../../api/budget-contacts';
import { rejectionTypesApi } from '../../api/rejection-types';
import { contactTypesApi, type ContactTypeForSite } from '../../api/contact-types';
import { useToast } from '../../components/ui/Toast';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { PdfViewer } from '../../components/budgets/PdfViewer';
import { formatCurrency } from '../../utils/format';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function contactNarrative(contact: BudgetContactDto): string {
  if (contact.triggersRejection) return 'El paciente rechazó el presupuesto por teléfono.';
  if (contact.isNoAnswer) return 'El paciente no contestó la llamada.';
  if (contact.scheduledAt) return `Llamada programada para el ${formatDateTime(contact.scheduledAt)}.`;
  return contact.notes || `Contacto registrado: ${contact.contactTypeName}.`;
}

function ContactTimeline({ contacts, isLoading }: { contacts: BudgetContactDto[]; isLoading: boolean }) {
  if (isLoading) return (
    <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
      <Loader2 className="h-4 w-4 animate-spin" /> Cargando historial...
    </div>
  );
  if (contacts.length === 0) return (
    <p className="text-sm text-gray-400 italic py-4">Sin contactos registrados aún.</p>
  );
  return (
    <div className="space-y-3">
      {contacts.map((contact, idx) => (
        <div key={contact.id} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
              contact.triggersRejection ? 'bg-gray-200 text-gray-600' :
              contact.isNoAnswer ? 'bg-gray-200 text-gray-600' :
              contact.scheduledAt ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
            }`}>
              {contact.triggersRejection ? <PhoneOff className="h-3.5 w-3.5" /> :
               contact.isNoAnswer ? <PhoneMissed className="h-3.5 w-3.5" /> :
               contact.scheduledAt ? <Calendar className="h-3.5 w-3.5" /> :
               <CheckCircle className="h-3.5 w-3.5" />}
            </div>
            {idx < contacts.length - 1 && <div className="w-0.5 flex-1 bg-gray-200 mt-1" />}
          </div>
          <div className="pb-3 flex-1 min-w-0">
            <p className="text-sm text-gray-800">{contactNarrative(contact)}</p>
            {contact.scheduledAt && contact.notes && (
              <p className="text-xs text-gray-500 mt-0.5 italic">Mensaje: "{contact.notes}"</p>
            )}
            {!contact.scheduledAt && contact.notes && contact.triggersRejection && (
              <p className="text-xs text-gray-500 mt-0.5 italic">"{contact.notes}"</p>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {contact.createdByName || 'Sistema'} · {formatDateTime(contact.createdAt)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ManageModal({ budget, contactTypes, isOpen, onClose, onSaved }: {
  budget: FollowUpBudgetDto | null;
  contactTypes: ContactTypeForSite[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [contacts, setContacts] = useState<BudgetContactDto[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [notes, setNotes] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showRejectionConfirm, setShowRejectionConfirm] = useState(false);
  const [formErrors, setFormErrors] = useState<{ notes?: string; schedule?: string }>({});
  const [rejectionOptions, setRejectionOptions] = useState<{ id: string; name: string; requiresNote: boolean }[]>([]);
  const [selectedRejectionId, setSelectedRejectionId] = useState('');
  const [rejectionNote, setRejectionNote] = useState('');
  const [loadingRejections, setLoadingRejections] = useState(false);
  const [rejectionError, setRejectionError] = useState<string | null>(null);

  const selectedType = contactTypes.find(t => t.id === selectedTypeId) || null;
  const isMessageType = selectedType && !selectedType.requiresSchedule && !selectedType.isNoAnswer && !selectedType.triggersRejection;
  const todayStr = new Date().toISOString().split('T')[0];

  const getMinTime = () => {
    if (scheduledDate !== todayStr) return '00:00';
    const now = new Date();
    now.setMinutes(now.getMinutes() + 1);
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!isOpen || !budget) return;
    setIsLoadingContacts(true);
    setSelectedTypeId(''); setNotes(''); setScheduledDate(''); setScheduledTime('');
    setShowRejectionConfirm(false); setFormErrors({}); setSelectedRejectionId(''); setRejectionNote(''); setRejectionError(null);
    budgetContactsApi.getContacts(budget.id)
      .then(setContacts).catch(() => setContacts([]))
      .finally(() => setIsLoadingContacts(false));
  }, [isOpen, budget]);

  useEffect(() => {
    if (!showRejectionConfirm || rejectionOptions.length > 0) return;
    let cancelled = false;
    setLoadingRejections(true);
    rejectionTypesApi.getForSite()
      .then((d) => { if (!cancelled) setRejectionOptions(d); })
      .catch(() => { if (!cancelled) toast.error('Error', 'No se pudieron cargar los motivos de rechazo'); })
      .finally(() => { if (!cancelled) setLoadingRejections(false); });
    return () => { cancelled = true; };
  }, [showRejectionConfirm, rejectionOptions.length]);

  const validate = (): boolean => {
    const errs: { notes?: string; schedule?: string } = {};
    if (isMessageType && !notes.trim()) errs.notes = 'El mensaje es obligatorio';
    if (selectedType?.requiresSchedule) {
      if (!scheduledDate || !scheduledTime) errs.schedule = 'La fecha y hora son obligatorias';
      else if (new Date(`${scheduledDate}T${scheduledTime}`) <= new Date()) errs.schedule = 'La fecha y hora deben ser futuras';
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!selectedTypeId || !budget) return;
    if (!validate()) return;
    if (selectedType?.triggersRejection && !showRejectionConfirm) { setShowRejectionConfirm(true); return; }
    if (selectedType?.triggersRejection) {
      if (!selectedRejectionId) { setRejectionError('Selecciona un motivo de rechazo'); return; }
      const opt = rejectionOptions.find((o) => o.id === selectedRejectionId);
      if (opt?.requiresNote && !rejectionNote.trim()) { setRejectionError('Este motivo requiere una nota'); return; }
      setRejectionError(null);
    }
    let scheduledAt: string | undefined;
    if (selectedType?.requiresSchedule && scheduledDate && scheduledTime) {
      scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
    }
    setIsSaving(true);
    try {
      const newContact = await budgetContactsApi.createContact(budget.id, {
        contactTypeId: selectedTypeId, notes: notes.trim() || undefined, scheduledAt,
        rejectionTypeId: selectedType?.triggersRejection ? selectedRejectionId : undefined,
        rejectionNotes: selectedType?.triggersRejection ? (rejectionNote.trim() || undefined) : undefined,
      });
      toast.success('Contacto registrado',
        selectedType?.triggersRejection ? 'El presupuesto ha sido rechazado' : 'Contacto registrado correctamente'
      );
      setContacts(prev => [newContact, ...prev]);
      setSelectedTypeId(''); setNotes(''); setScheduledDate(''); setScheduledTime('');
      setShowRejectionConfirm(false); setFormErrors({}); setSelectedRejectionId(''); setRejectionNote(''); setRejectionError(null);
      onSaved();
      if (selectedType?.triggersRejection) onClose();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo registrar el contacto');
    } finally { setIsSaving(false); }
  };

  if (!budget) return null;

  return (
    <Modal isOpen={isOpen} onClose={() => !isSaving && onClose()}
      title={budget.patientName}
      subtitle={`${budget.budgetNumber} · ${formatCurrency(budget.total)}`}
      size="xl">
      {showRejectionConfirm ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-900">Confirmar rechazo del presupuesto</p>
              <p className="text-sm text-amber-800 mt-1">
                El presupuesto <strong>{budget.budgetNumber}</strong> quedará rechazado. Indica el motivo. Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo de rechazo *</label>
            {loadingRejections ? (
              <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando motivos...</div>
            ) : (
              <select value={selectedRejectionId}
                onChange={(e) => { setSelectedRejectionId(e.target.value); setRejectionError(null); }}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0]">
                <option value="">Selecciona un motivo...</option>
                {rejectionOptions.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            )}
            {!loadingRejections && rejectionOptions.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">No hay motivos de rechazo asignados a este centro.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas{rejectionOptions.find((o) => o.id === selectedRejectionId)?.requiresNote ? ' *' : ' (opcional)'}
            </label>
            <textarea value={rejectionNote}
              onChange={(e) => { setRejectionNote(e.target.value); setRejectionError(null); }}
              rows={3} maxLength={2000}
              placeholder="Motivo detallado / observaciones..."
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0] resize-none" />
          </div>
          {rejectionError && <p className="text-xs text-red-500">{rejectionError}</p>}
          <ModalFooter>
            <button type="button" onClick={() => setShowRejectionConfirm(false)} disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              <X className="h-4 w-4" /> Cancelar
            </button>
            <button type="button" onClick={handleSave} disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl disabled:opacity-50 flex items-center gap-2">
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</> : <><PhoneOff className="h-4 w-4" />Confirmar rechazo</>}
            </button>
          </ModalFooter>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 min-h-[420px]">
          {/* Columna izquierda — Historial */}
          <div className="flex flex-col lg:pr-6 lg:border-r border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-700">Historial de contactos</h3>
              {contacts.length > 0 && (
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{contacts.length}</span>
              )}
            </div>
            <div className="flex-1 overflow-y-auto max-h-[380px] pr-1">
              <ContactTimeline contacts={contacts} isLoading={isLoadingContacts} />
            </div>
          </div>

          {/* Columna derecha — Formulario */}
          <div className="flex flex-col gap-4 lg:pl-6 mt-4 lg:mt-0">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-gray-700">Registrar contacto</h3>
            </div>
            {budget.patientPhone && (
              <a href={`tel:${budget.patientPhone}`}
                className="inline-flex items-center gap-2 text-sm text-[#008DD0] hover:text-[#007ab8] font-medium w-fit">
                <Phone className="h-4 w-4" /> {budget.patientPhone}
              </a>
            )}
            <div className="space-y-2">
              {contactTypes.map(type => (
                <label key={type.id} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer transition-colors ${
                  selectedTypeId === type.id ? 'border-[#008DD0] bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}>
                  <input type="radio" name="contactType" value={type.id}
                    checked={selectedTypeId === type.id}
                    onChange={() => { setSelectedTypeId(type.id); setShowRejectionConfirm(false); setFormErrors({}); }}
                    className="text-[#008DD0]" />
                  {type.triggersRejection ? <PhoneOff className="h-4 w-4 text-gray-400 shrink-0" /> :
                   type.isNoAnswer ? <PhoneMissed className="h-4 w-4 text-gray-400 shrink-0" /> :
                   type.requiresSchedule ? <Calendar className="h-4 w-4 text-gray-400 shrink-0" /> :
                   <MessageSquare className="h-4 w-4 text-gray-400 shrink-0" />}
                  <span className="text-sm font-medium text-gray-800 flex-1">{type.name}</span>
                  {type.triggersRejection && <span className="text-xs text-gray-400 italic shrink-0">Rechaza presupuesto</span>}
                  {type.requiresSchedule && <span className="text-xs text-gray-400 italic shrink-0">Requiere fecha</span>}
                </label>
              ))}
              {contactTypes.length === 0 && (
                <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-xl">No hay tipos de contacto asignados a este centro.</p>
              )}
            </div>
            {selectedType?.requiresSchedule && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fecha y hora para llamar *</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Fecha</label>
                    <input type="date" value={scheduledDate} min={todayStr}
                      onChange={e => { setScheduledDate(e.target.value); setFormErrors(p => ({ ...p, schedule: undefined })); }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0]" />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Hora</label>
                    <input type="time" value={scheduledTime}
                      min={scheduledDate === todayStr ? getMinTime() : undefined}
                      onChange={e => { setScheduledTime(e.target.value); setFormErrors(p => ({ ...p, schedule: undefined })); }}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0]" />
                  </div>
                </div>
                {formErrors.schedule && <p className="mt-1 text-xs text-red-500">{formErrors.schedule}</p>}
              </div>
            )}
            {selectedType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isMessageType ? 'Mensaje *' : selectedType.requiresSchedule ? 'Mensaje para quien llame' : 'Notas adicionales'}
                </label>
                <textarea value={notes}
                  onChange={e => { setNotes(e.target.value); setFormErrors(p => ({ ...p, notes: undefined })); }}
                  rows={3} maxLength={2000}
                  placeholder={
                    selectedType.triggersRejection ? 'Motivo del rechazo (opcional)...' :
                    selectedType.isNoAnswer ? 'Observaciones (opcional)...' :
                    selectedType.requiresSchedule ? 'Información para quien llame más tarde...' :
                    'Resumen de la conversación...'
                  }
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0] resize-none ${
                    formErrors.notes ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                {formErrors.notes && <p className="mt-1 text-xs text-red-500">{formErrors.notes}</p>}
              </div>
            )}
            <div className="mt-auto pt-2">
              <button type="button" onClick={handleSave}
                disabled={isSaving || !selectedTypeId || (selectedType?.requiresSchedule && (!scheduledDate || !scheduledTime))}
                className="w-full px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                {isSaving ? <><Loader2 className="h-4 w-4 animate-spin" />Guardando...</>
                  : selectedType?.triggersRejection ? <><PhoneOff className="h-4 w-4" />Registrar y rechazar</>
                  : <><Save className="h-4 w-4" />Guardar contacto</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function PdfViewerModal({ budgetId, budgetNumber, patientEmail, isOpen, onClose }: {
  budgetId: string; budgetNumber: string; patientEmail: string; isOpen: boolean; onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const toast = useToast();

  const handleOpenResend = () => {
    setEmailInput(patientEmail || '');
    setShowEmailModal(true);
  };

  const handleResend = async () => {
    if (!emailInput.trim()) return;
    setIsSendingEmail(true);
    try {
      await budgetsApi.resend(budgetId, emailInput.trim());
      toast.success('Email enviado', 'Presupuesto enviado correctamente');
      setShowEmailModal(false);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo enviar el email');
    } finally { setIsSendingEmail(false); }
  };

  useEffect(() => {
    if (!isOpen) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl(null); setPdfError(null); setShowEmailModal(false); return;
    }
    setIsLoadingPdf(true); setPdfError(null);
    const token = localStorage.getItem('imi_access_token') || '';
    fetch(`/api/v1/budgets/${budgetId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(`Error ${r.status}`); return r.blob(); })
      .then(blob => setBlobUrl(URL.createObjectURL(blob)))
      .catch(e => setPdfError(e.message || 'No se pudo cargar el PDF'))
      .finally(() => setIsLoadingPdf(false));
  }, [isOpen, budgetId]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size="xl" showCloseButton={false}>
      <div className="-mx-6 -mt-6 mb-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[#008DD0]/10 border border-[#008DD0]/20 flex items-center justify-center shrink-0">
              <FileText className="h-3.5 w-3.5 text-[#008DD0]" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">Presupuesto</p>
              <p className="text-xs text-gray-400">{budgetNumber}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {patientEmail && (
              <button onClick={handleOpenResend}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-lg transition-colors">
                <Mail className="h-3.5 w-3.5" /> Reenviar email
              </button>
            )}
            {blobUrl && (
              <a href={blobUrl} download={`presupuesto-${budgetNumber}.pdf`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <Download className="h-3.5 w-3.5" /> Descargar
              </a>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      {isLoadingPdf && (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
          <p className="text-sm text-gray-400">Cargando documento...</p>
        </div>
      )}
      {pdfError && (
        <div className="flex flex-col items-center justify-center py-16">
          <p className="text-sm text-red-500">{pdfError}</p>
        </div>
      )}
      {blobUrl && !isLoadingPdf && (
        <div className="-mx-6 -mb-6">
          <PdfViewer url={blobUrl} />
        </div>
      )}

      {/* Modal reenviar email */}
      <Modal isOpen={showEmailModal} onClose={() => !isSendingEmail && setShowEmailModal(false)}
        title="Reenviar presupuesto" subtitle="Confirma o modifica el email de destino" size="sm">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={emailInput}
              onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !isSendingEmail && handleResend()}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#008DD0]"
              placeholder="email@ejemplo.com" autoFocus />
          </div>
          <ModalFooter>
            <button type="button" onClick={() => setShowEmailModal(false)} disabled={isSendingEmail}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              <X className="h-4 w-4" /> Cancelar
            </button>
            <button type="button" onClick={handleResend}
              disabled={isSendingEmail || !emailInput.trim()}
              className="px-4 py-2.5 text-sm font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-xl disabled:opacity-50 flex items-center gap-2">
              {isSendingEmail
                ? <><Loader2 className="h-4 w-4 animate-spin" />Enviando...</>
                : <><Mail className="h-4 w-4" />Enviar</>}
            </button>
          </ModalFooter>
        </div>
      </Modal>
    </Modal>
  );
}

function BudgetRow({ budget, contactTypes, onRefresh }: {
  budget: FollowUpBudgetDto;
  contactTypes: ContactTypeForSite[];
  onRefresh: () => void;
}) {
  const [manageModal, setManageModal] = useState(false);
  const [pdfModal, setPdfModal] = useState(false);

  const hasScheduled = !!budget.nextScheduledAt;
  const isScheduledSoon = hasScheduled && new Date(budget.nextScheduledAt!).getTime() - Date.now() < 2 * 3600 * 1000;
  const hasOverdue = budget.nextScheduledOverdue;

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-900">{budget.patientName || '—'}</span>
              <span className="text-xs text-gray-400 font-mono">{budget.budgetNumber}</span>
              {budget.treatmentName && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{budget.treatmentName}</span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-1.5 flex-wrap">
              {budget.patientPhone ? (
                <a href={`tel:${budget.patientPhone}`}
                  className="flex items-center gap-1.5 text-sm text-[#008DD0] hover:text-[#007ab8] font-medium">
                  <Phone className="h-3.5 w-3.5" /> {budget.patientPhone}
                </a>
              ) : (
                <span className="flex items-center gap-1.5 text-sm text-gray-400">
                  <Phone className="h-3.5 w-3.5" /> Sin teléfono
                </span>
              )}
              <span className="text-sm text-gray-600 font-medium">{formatCurrency(budget.total)}</span>
              <span className="text-xs text-gray-400">Enviado {formatDate(budget.sentAt)}</span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              budget.contactCount > 0 ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-400'
            }`}>
              {budget.contactCount > 0 ? `${budget.contactCount} intento${budget.contactCount !== 1 ? 's' : ''}` : 'Sin contactos'}
            </span>
            {hasScheduled && (
              <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                isScheduledSoon ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
              }`}>
                <Clock className="h-3 w-3" /> {formatDateTime(budget.nextScheduledAt)}
              </span>
            )}
            {hasOverdue && (
              <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                <AlertTriangle className="h-3 w-3" /> Llamada vencida
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
          <button onClick={() => setManageModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[#008DD0] hover:bg-[#007ab8] rounded-lg transition-colors">
            <Phone className="h-3.5 w-3.5" /> Gestionar
          </button>
          <button onClick={() => setPdfModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            <FileText className="h-3.5 w-3.5" /> Ver PDF
          </button>
        </div>
      </div>
      <ManageModal budget={budget} contactTypes={contactTypes} isOpen={manageModal}
        onClose={() => setManageModal(false)} onSaved={onRefresh} />
      <PdfViewerModal budgetId={budget.id} budgetNumber={budget.budgetNumber}
        patientEmail={budget.patientEmail} isOpen={pdfModal} onClose={() => setPdfModal(false)} />
    </>
  );
}

export function BudgetFollowUpPage() {
  const toast = useToast();
  const [budgets, setBudgets] = useState<FollowUpBudgetDto[]>([]);
  const [contactTypes, setContactTypes] = useState<ContactTypeForSite[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [b, ct] = await Promise.all([budgetContactsApi.getFollowUp(), contactTypesApi.getForSite()]);
      setBudgets(b); setContactTypes(ct);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cargar el seguimiento');
    } finally { setIsLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Seguimiento de presupuestos</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isLoading ? 'Cargando...' : `${budgets.length} presupuesto${budgets.length !== 1 ? 's' : ''} pendiente${budgets.length !== 1 ? 's' : ''} de respuesta`}
          </p>
        </div>
        <button onClick={load}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors">
          <RefreshCw className="h-4 w-4" /> Actualizar
        </button>
      </div>
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : budgets.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-7 w-7 text-green-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">Todo al día</h3>
          <p className="text-sm text-gray-500">No hay presupuestos enviados pendientes de respuesta en este centro.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {budgets.map(b => (
            <BudgetRow key={b.id} budget={b} contactTypes={contactTypes} onRefresh={load} />
          ))}
        </div>
      )}
    </div>
  );
}

export default BudgetFollowUpPage;
